import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, isCurrencyCode } from '@/lib/money/money'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Risk & Compliance — the two reads that exist.
 *
 *   `rk-limits` ← GET /v1/admin/limit-counters
 *   `rk-inflow` ← GET /v1/admin/approved-inflow-sources
 *
 * # Absent ceilings, and why a zero here is the dangerous value
 *
 * `ceiling_amount_minor` is ABSENT when no policy resolves for a customer. That
 * is a real state — nobody has set a limit — and it is not a ceiling of zero.
 * Rendered as `0` it would read as "over the limit on everything", which is the
 * opposite of the truth and would send an operator to restrict somebody who is
 * simply ungoverned.
 *
 * `consumed_basis_points` is absent for the same reason: there is nothing to
 * divide by. A zero would read as "nothing consumed", which is also false — the
 * consumption is known, the share of a ceiling is not.
 *
 * # The unexplained share is a separate column on purpose
 *
 * A customer at their ceiling entirely through unrecognised inflow is a
 * different case from one there through known platform income, and the two want
 * different responses. Both are shown; neither is summed into the other.
 */

const AUTHORITY = 'operational'
const LIMIT = 100

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

function whole(v: unknown): number | null {
  if (typeof v === 'number' && Number.isSafeInteger(v)) return v
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) {
    const n = Number(v.trim())
    return Number.isSafeInteger(n) ? n : null
  }
  return null
}

function rowsOf(p: Record<string, unknown>, key: string): readonly unknown[] {
  const v = p[key]
  return Array.isArray(v) ? v : []
}

/** An amount from a bare minor-unit field plus a sibling currency. */
function amount(minor: unknown, currency: string, absent: string): Cell {
  if (minor === null || minor === undefined) return absent
  if (!isCurrencyCode(currency)) return '— no currency stated'
  const big = exactMinorBig(minor)
  if (big === null) return '— not an exact integer'
  if (big > BigInt(Number.MAX_SAFE_INTEGER) || big < -BigInt(Number.MAX_SAFE_INTEGER)) {
    return '— beyond the exact range of an amount cell'
  }
  return [Number(big), currency]
}

function table(
  title: string, cols: readonly Column[], body: readonly (readonly Cell[])[], footer: string,
): TableBlock {
  return { t: 'table', title, src: 'ledger', facets: false, cols, rows: body, footer }
}

function refusal(error: unknown, source: string, what: string): ScreenData {
  if (error instanceof DOMException && error.name === 'AbortError') throw error
  const api = error instanceof ApiError ? error : null
  const reason = api === null
    ? 'The backend could not be reached.'
    : api.status === 404
      ? 'This endpoint is not available on this deployment.'
      : `The backend answered ${String(api.status)}${api.code === null ? '' : ` (${api.code})`}.`
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: what,
      title: `${what} could not be loaded`,
      body: reason,
      ref: api?.decisionRef === null || api?.decisionRef === undefined
        ? ''
        : `decision_ref ${api.decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

// ---------------------------------------------------------------------------
// rk-limits
// ---------------------------------------------------------------------------

export const LIMIT_COUNTERS_PATH = `/limit-counters?limit=${String(LIMIT)}`

const COUNTERS_SOURCE = 'postgres · limit_counters (GET /v1/admin/limit-counters)'

const COUNTER_COLS: readonly Column[] = [
  ['Customer', 'm', '100px'],
  ['Direction', 'x', '90px'],
  ['Period', 'x', '110px'],
  ['Consumed', 'a', '130px'],
  ['Ceiling', 'a', '130px'],
  ['Share', 'n', '96px'],
  ['Unexplained', 'a', '130px'],
  ['Policy', 'm', 'minmax(130px,1fr)'],
]

export function composeLimitCounters(payload: unknown, readAt: Date): ScreenData {
  if (!isObject(payload)) return refusal(null, COUNTERS_SOURCE, 'Limits and counters')

  const body: Cell[][] = []
  let ungoverned = 0
  let unreadable = 0
  for (const raw of rowsOf(payload, 'counters')) {
    if (!isObject(raw)) { unreadable += 1; continue }
    const cur = text(raw['currency'])
    const ceiling = raw['ceiling_amount_minor']
    const bps = whole(raw['consumed_basis_points'])
    if (ceiling === undefined || ceiling === null) ungoverned += 1

    body.push([
      String(whole(raw['customer_id']) ?? '—'),
      text(raw['direction']) === '' ? '— not stated' : text(raw['direction']),
      text(raw['period']) === '' ? '— not stated' : text(raw['period']),
      amount(raw['amount_minor'], cur, '— not stated'),
      // Absent is NOT zero: no policy resolved, so this customer is ungoverned
      // rather than at a ceiling of nothing.
      amount(ceiling, cur, '— no policy resolves'),
      bps === null ? '— no ceiling' : `${String(Math.round(bps / 100))}%`,
      amount(raw['unexplained_amount_minor'], cur, '— not stated'),
      text(raw['policy_ref']) === ''
        ? '— none'
        : `${text(raw['policy_ref'])} v${String(whole(raw['policy_version']) ?? 0)}`,
    ])
  }

  const blocks: Block[] = [
    table(
      'Limits and counters',
      COUNTER_COLS,
      body,
      `${String(body.length)} counter(s)`
      + (ungoverned === 0 ? '' : ` · ${String(ungoverned)} with no policy resolved`)
      + (unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`),
    ),
  ]

  return {
    blocks,
    provenance: { kind: 'live', source: COUNTERS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
  }
}

export const loadLimitCounters: ScreenLoader = async (api) => {
  try {
    return composeLimitCounters(await api.get<unknown>(LIMIT_COUNTERS_PATH), new Date())
  } catch (error) {
    return refusal(error, COUNTERS_SOURCE, 'Limits and counters')
  }
}

// ---------------------------------------------------------------------------
// rk-inflow
// ---------------------------------------------------------------------------

export const INFLOW_SOURCES_PATH = '/approved-inflow-sources'

const INFLOW_SOURCE = 'postgres · approved_inflow_sources (GET /v1/admin/approved-inflow-sources)'

const INFLOW_COLS: readonly Column[] = [
  ['Platform', 'x', 'minmax(140px,1fr)'],
  ['Payer identity', 'x', 'minmax(180px,1.4fr)'],
  ['Currency', 'x', '90px'],
  ['Annex', 'm', 'minmax(130px,1fr)'],
  ['State', 'c', '130px'],
]

export function composeInflowSources(payload: unknown, readAt: Date): ScreenData {
  if (!isObject(payload)) return refusal(null, INFLOW_SOURCE, 'Approved inflow sources')

  const body: Cell[][] = []
  let pending = 0
  let unreadable = 0
  for (const raw of rowsOf(payload, 'sources')) {
    if (!isObject(raw)) { unreadable += 1; continue }
    const annexPending = raw['annex_pending'] === true
    if (annexPending) pending += 1

    body.push([
      text(raw['platform_id']) === '' ? '— not stated' : text(raw['platform_id']),
      text(raw['payer_identity']) === '' ? '— not stated' : text(raw['payer_identity']),
      text(raw['currency']) === '' ? '— not stated' : text(raw['currency']),
      text(raw['annex_ref']) === '' ? '— none cited' : text(raw['annex_ref']),
      annexPending
        ? (['annex pending', 'a'] as ChipCell)
        : (['approved', 'g'] as ChipCell),
    ])
  }

  const blocks: Block[] = [
    table(
      'Approved inflow sources',
      INFLOW_COLS,
      body,
      `${String(body.length)} source(s)`
      + (pending === 0 ? '' : ` · ${String(pending)} awaiting an annex`)
      + (unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`),
    ),
  ]

  return {
    blocks,
    provenance: { kind: 'live', source: INFLOW_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
  }
}

export const loadInflowSources: ScreenLoader = async (api) => {
  try {
    return composeInflowSources(await api.get<unknown>(INFLOW_SOURCES_PATH), new Date())
  } catch (error) {
    return refusal(error, INFLOW_SOURCE, 'Approved inflow sources')
  }
}
